// result-verifier.ts
//
// The verifier is the SOLE decider of "done" (§5.4 architecture note):
// `outcome: done` + exit 0 + the model's own words are only INPUT, never
// trusted directly. Mandatory order (phase 06 Agent B brief):
//   1. wait groupReaped
//   2. HEAD not unexpectedly changed (changed => recovering; a worker
//      commit is detected the same way => recovering, nothing deleted —
//      checked BEFORE binding cleanup so a recovering verdict never
//      deletes the binding)
//   3. rehash/cleanup binding (contract_error on mismatch/missing)
//   4. inventory diff (baseline vs current worktree)
//   5. every changed path inside ticket's allowed_paths
//   6. run_id / ticket_revision in result match expectations; a
//      review-local attempt must also declare `review_report` (P21)
//   7. report path inside staging dir + file exists
//   8. recipe comes from the approved verification-profile AND is the
//      ticket's own recipe (worker-supplied recipe replacing it => reject);
//      the controller's run decides, never the declared exit_code
//   9. for a testcase ticket: RED required, and it must fail for MISSING
//      BEHAVIOR, not import/syntax errors
//  10. baseline regression surfaced by relatedRecipeChecks => reject
//  11. disabled assertions appearing in the diff => reject
//  12. after applying artifact proposals, rerun related checks

import { captureBaselineInventory, diffInventory } from "./attempt-baseline-inventory.js";
import type { AutopilotResult } from "./autopilot-result-schema.js";
import { rehashAndCleanupBinding } from "./binding-rehash-and-cleanup.js";
import { partitionControllerOwnedPaths } from "./controller-owned-paths.js";
import { checkChangedFilesInScope } from "./result-verifier-checks.js";
import { firstContractFieldRejection } from "./result-verifier-contract-field-checks.js";
import { evaluateEvidence, scanForDisabledAssertions } from "./result-verifier-evidence.js";
import { detectUnexpectedHeadChange } from "./result-verifier-head-check.js";
import {
  controllerOwnedEditInDiffReason,
  profileChangedDuringRecipesReason,
  readVerificationProfileBytes,
} from "./result-verifier-profile-tamper-checks.js";
import { sensitivePathWithoutAcceptanceMention } from "./result-verifier-sensitive-path-checks.js";
import type { VerifierVerdict, VerifyAttemptResultInput } from "./result-verifier-types.js";

// Types live in result-verifier-types.ts; steps 6-6e in
// result-verifier-contract-field-checks.ts; profile-tamper helpers in
// result-verifier-profile-tamper-checks.ts (plan 20260924-0205). Re-exported
// so `import … from "./result-verifier.js"` keeps working.
export type {
  RelatedRecipeCheck,
  VerifierVerdict,
  VerifierVerdictKind,
  VerifyAttemptResultInput,
} from "./result-verifier-types.js";

function rejected(reasons: string[]): VerifierVerdict {
  return { kind: "rejected", reasons };
}

export async function verifyAttemptResult(
  input: VerifyAttemptResultInput,
): Promise<VerifierVerdict> {
  const result = input.result as AutopilotResult;

  // 1. Wait for every subprocess of the attempt to be reaped before ANY diffing.
  if (!input.groupReaped) {
    return { kind: "recovering", reasons: ["group_not_reaped_yet: chưa thể diff an toàn"] };
  }

  // 2. HEAD must not have moved unexpectedly (covers a forbidden worker
  // commit too) — checked BEFORE binding cleanup so a worker-commit
  // recovering verdict never deletes the binding (§9.4: "phát hiện commit
  // của worker => RECOVERING, không xoá gì").
  const headMoved = await detectUnexpectedHeadChange(
    input.repoDir,
    input.baselineInventory.headSha,
  );
  if (headMoved) {
    return {
      kind: "recovering",
      reasons: ["unexpected_head_change: HEAD đổi ngoài dự kiến (có thể worker đã tự commit)"],
    };
  }

  // 3. Re-hash + cleanup binding.
  const rehash = await rehashAndCleanupBinding({
    missionWorkspace: input.repoDir,
    bindingPath: input.bindingPath,
    bindingHash: input.bindingHash,
    groupReaped: input.groupReaped,
  });
  if (rehash.status === "contract_error") {
    return { kind: "contract_error", reasons: [rehash.reason ?? "binding_contract_error"] };
  }

  // 4. Inventory diff.
  const currentInventory = await captureBaselineInventory(input.repoDir);
  const diff = diffInventory(input.baselineInventory, currentInventory);
  // Bug E (dogfood vòng 4, T-0201 attempt 2-3): a leftover binding file from
  // a PRIOR attempt can end up tracked in this attempt's own baseline (a
  // later `run`'s bootstrap commit swept it in before cleanup ever ran for
  // it — see controller-owned-paths.ts's doc comment). Controller-owned
  // paths are never any ticket's `allowed_paths` to cover either way, so
  // they are excluded from the scope check regardless of whether the
  // mission worktree's `.gitignore` (mission-workspace.ts) already keeps
  // them out of the inventory in the first place — belt and suspenders, one
  // shared source of truth.
  const partitioned = partitionControllerOwnedPaths([
    ...diff.added,
    ...diff.modified,
    ...diff.deleted,
  ]);
  const touchedPaths = partitioned.kept;

  // 5. Every touched path must be in allowed_paths.
  const scopeReason = checkChangedFilesInScope(touchedPaths, input.allowedPaths);
  if (scopeReason) return rejected([scopeReason]);

  // 5b. A controller-owned path edited in the diff — see
  // result-verifier-profile-tamper-checks.ts for why every owned path but the
  // binding directory is rejected.
  const ownedEditReason = controllerOwnedEditInDiffReason(partitioned.excluded);
  if (ownedEditReason) return rejected([ownedEditReason]);

  // 5c (06g R1d, debate Q4): hidden test/CI config in the diff needs an
  // acceptance mention — decided BEFORE any recipe can execute that config.
  const sensitiveReason = sensitivePathWithoutAcceptanceMention(
    touchedPaths,
    input.ticketAcceptance,
  );
  if (sensitiveReason) return rejected([sensitiveReason]);

  // 6-6f. Result contract fields vs controller expectations (run_id,
  // ticket_revision, input_hash, review_report, evidence-required, acceptance
  // rows, created tickets' acceptance) — result-verifier-contract-field-checks.ts. Runs BEFORE
  // evaluateEvidence: its loop alone lets an EMPTY evidence[] through, so
  // emptiness is caught here; evaluateEvidence adds the second layer (SOL-04:
  // evidence must name the ticket's own recipe).
  const contractReason = firstContractFieldRejection(result, input);
  if (contractReason) return rejected([contractReason]);

  // Audit F3 (link 1): step 5b above catches a profile edit that shows up in
  // the INVENTORY DIFF — but that diff was captured at step 4, before any
  // recipe ran. A recipe rewriting the profile at steps 7-9 therefore slips
  // past it purely on timing. Snapshot the profile now, compare after.
  const profileBefore = await readVerificationProfileBytes(input.repoDir);

  // 7 + 8 + 9: report scope, recipe approval, recipe execution/interpretation.
  const evidenceReasons = await evaluateEvidence({
    evidence: result.evidence,
    stagingDir: input.stagingDir,
    approvedRecipes: input.approvedRecipes,
    expectedRecipeId: input.expectedRecipeId,
    runRecipe: input.runRecipe,
    isTestcaseAttempt: input.isTestcaseAttempt,
  });

  // Checked BEFORE the evidence reasons are returned — a recipe that rewrote
  // the rulebook makes every verdict from that run suspect.
  const profileChangedReason = profileChangedDuringRecipesReason(
    profileBefore,
    await readVerificationProfileBytes(input.repoDir),
  );
  if (profileChangedReason) return rejected([profileChangedReason]);

  if (evidenceReasons.length > 0) return rejected(evidenceReasons);

  // 9b. Disabled assertions in the diff (touched test files' current content).
  const disabledReason = await scanForDisabledAssertions(input.repoDir, [
    ...diff.added,
    ...diff.modified,
  ]);
  if (disabledReason) return rejected([disabledReason]);

  // 10. Baseline regression via related checks (slice B breaking while
  // verifying slice A) — run BEFORE declaring done.
  if (input.relatedRecipeChecks) {
    for (const check of input.relatedRecipeChecks) {
      const related = await check.runRecipe();
      if (related.exitCode !== 0) {
        return rejected([
          `baseline_regression_detected: ${check.recipe.recipe_id} => ${related.stdoutTail.trim()}`,
        ]);
      }
    }
  }

  return { kind: "done", reasons: [] };
}
